<script setup lang="ts">
import type { Form } from './blocks/electricityBills/ElectricityBills.schema'
import type { ErrorPayload } from './Formalization.types'
import type { CustomerRegistrationInput } from '~/store-v2/registration/useUpdateRegistration'
import type {
  StageFormalization,
} from '~/ui-blocks/Formalization/Formalization.types'
import { useToast } from '@solfacil/girassol'
import { toTypedSchema } from '@vee-validate/zod'
import isEqual from 'lodash/isEqual'
import { useForm } from 'vee-validate'
import * as zod from 'zod'

// Icons
import IconLock from '~icons/material-symbols/lock'
import CancelStep from '~/components/shared/CancelStep.vue'
import { useDistributorInstallationStateGuard } from '~/composables/equipment/useDistributorInstallationStateGuard'
import { provideFormalizationReadOnly } from '~/composables/financing/useFormalizationReadOnly'
import { useTerminalStatusLock } from '~/composables/financing/useTerminalStatusLock'
import { useAccessOpinionRequirement } from '~/composables/formalization/useAccessOpinionRequirement'
import { useDateProps } from '~/composables/proposal/useDateProps'
import { FEATURE_FLAGS } from '~/constants'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import useFinancing from '~/store-v2/financings/useFinancing'
import { FinancingStatus } from '~/store-v2/financings/useFinancings'
import useProject from '~/store-v2/projects/useProject'
import useRegistration from '~/store-v2/registration/useRegistration'
import useUpdateRegistration from '~/store-v2/registration/useUpdateRegistration'
import { Formalization } from '~/types/enum'
import { convertToISODate } from '~/utils/string'
import MultiAccountAutoConsumptionModal from '../Customer/blocks/MultiAccountAutoConsumptionModal.vue'
// blocks
import AddressSectionForm from './blocks/address/Address.form.vue'
import * as addressSchema from './blocks/address/Address.schema'
import DocumentationUnderAnalysisModal from './blocks/DocumentationUnderAnalysisModal.vue'
import ElectricityBillSectionForm from './blocks/electricityBill/ElectricityBill.form.vue'
import * as electricityBillSchema from './blocks/electricityBill/ElectricityBill.schema'
import ElectricityBillsSectionForm from './blocks/electricityBills/ElectricityBills.form.vue'
import * as electricityBillsSchema from './blocks/electricityBills/ElectricityBills.schema'
import PersonSectionForm from './blocks/person/Person.form.vue'
import * as personSchema from './blocks/person/Person.schema'
import { categorizeErrorsPF, getStatusTag, showErrorsFieldsPF } from './Formalization.utils'
import StatusDates from './StatusDates.vue'

// Props
const props = defineProps<{
  position: number
  stage: StageFormalization
  stageReproved: string
  triggerRefetch: () => Promise<void>
}>()

// initial base hooks
const { t } = useI18n()
const route = useRoute<any>()
const toast = useToast()

// initial variables and computed
const projectId = computed(() => route.params.id as string)
const blocked = ref(false)
const docUnderAnalysisModal = ref(false)
const isValidateCpfCustomer = ref(false)
const isValidateCpfGuarantor = ref(false)

const { multiAccountModal, showModal, closeModal } = useMultiAccountModal()

// domain hooks

const {
  data: project,
} = useProject(projectId)

const {
  data: financing,
} = useFinancing(projectId)

const { isTerminalLocked, isTerminalReadOnly, terminalTag } = useTerminalStatusLock(financing, 'customer_data', t)

// Terminal financing: nested uploads become view/download only.
provideFormalizationReadOnly(isTerminalReadOnly)

// Lock caused by another reproved stage; ignored in terminal financing so started steps can be viewed.
const isStageReprovedBlocked = computed(() => blocked.value && !isTerminalReadOnly.value)

const {
  data: registration,
} = useRegistration(projectId)

const {
  data: dataUpdateRegistration,
  mutateAsync: updateRegistration,
  isPending: isPendingUpdateRegistration,
} = useUpdateRegistration(projectId)

const { validate: validateDistributorInstallationState } = useDistributorInstallationStateGuard(projectId)

function showDistributorInstallationGuardError() {
  toast.createErrorToast({
    title: t('customer.distributor_installation_guard.mismatch.title'),
    description: t('customer.distributor_installation_guard.mismatch.description'),
  })
}

const {
  data: enableMultipleEnergyAccounts,
} = useFeatureFlag(FEATURE_FLAGS.ENABLE_MULTIPLE_ENERGY_ACCOUNTS, false, true)

// INITIAL FORMS
const customerDocumentationForm = useForm({
  initialValues: personSchema.getInitialValues(),
  validationSchema: toTypedSchema(personSchema.getSchema(t, isValidateCpfCustomer.value)),
  validateOnMount: false,
})

const guarantorDocumentationForm = useForm({
  initialValues: personSchema.getInitialValues(),
  validationSchema: toTypedSchema(personSchema.getSchema(t, isValidateCpfGuarantor.value)),
  validateOnMount: false,
})

const residencialAddressForm = useForm({
  initialValues: addressSchema.getInitialValues(),
  validationSchema: toTypedSchema(addressSchema.getSchema(t)),
  validateOnMount: false,
})

const installationAddressForm = useForm({
  initialValues: addressSchema.getInitialValues(),
  validationSchema: toTypedSchema(addressSchema.getSchema(t)),
  validateOnMount: false,
})

const guarantorAddressForm = useForm({
  initialValues: addressSchema.getInitialValues(),
  validationSchema: toTypedSchema(addressSchema.getSchema(t)),
  validateOnMount: false,
})

const electricityBillForm = useForm({
  initialValues: electricityBillSchema.getInitialValues(),
  validationSchema: toTypedSchema(electricityBillSchema.getSchema(t)),
  validateOnMount: false,
})

const electricityBillsForm = useForm({
  initialValues: electricityBillsSchema.getInitialValues(),
  validationSchema: toTypedSchema(electricityBillsSchema.getSchema(t)),
  validateOnMount: false,
})

const radiosForms = useForm({
  initialValues: {
    client_local_address: registration.value?.project?.residencial_is_the_same_of_plant || true,
    rural_area: registration.value?.project?.address?.is_rural || false,
    has_other_beneficiary_units: (registration.value?.has_other_beneficiary_units as boolean) || false,
  },
  validationSchema: toTypedSchema(zod.object({
    client_local_address: zod.boolean().nullable().or(zod.undefined()),
    rural_area: zod.boolean().nullable().or(zod.undefined()),
    has_other_beneficiary_units: zod.boolean().nullable().or(zod.undefined()),
  })),
  validateOnMount: false,
})

// fields
const [clientLocalAddressModel, clientLocalAddressModeProps] = radiosForms.defineField('client_local_address')
const [ruralAreaModel, ruralAreaProps] = radiosForms.defineField('rural_area')
const [hasOtherBeneficiaryUnitsModel] = radiosForms.defineField('has_other_beneficiary_units')

// Computed properties for v-model to avoid TypeScript issues
const clientLocalAddressComputed = computed({
  get: () => clientLocalAddressModel.value ?? false,
  set: (value: boolean) => {
    clientLocalAddressModel.value = value
  },
})

const ruralAreaComputed = computed({
  get: () => ruralAreaModel.value ?? false,
  set: (value: boolean) => {
    ruralAreaModel.value = value
  },
})

// Installation site: the customer address when it is the same place, otherwise the installation card (DC-2)
const installationLocationForm = computed(() =>
  radiosForms.values.client_local_address ? residencialAddressForm : installationAddressForm,
)

const {
  mode: accessOpinionMode,
  isEvaluating: isAccessOpinionEvaluating,
} = useAccessOpinionRequirement(
  projectId,
  computed(() => installationLocationForm.value.values.city),
  computed(() => installationLocationForm.value.values.state),
)

// The access opinion goes in the registration only while its field is visible (DC-4, DC-17)
const isAccessOpinionVisible = computed(() =>
  accessOpinionMode.value === 'SHOW'
  || accessOpinionMode.value === 'SHOW_REQUIRED'
  || !!electricityBillForm.errors.value.grid_access_approval_document
  || !!registration.value?.errors?.some(error => error.field === 'project.documents.grid_access_approval'),
)

// RADIOS
const radiosRuralArea = [
  {
    name: 'ruralArea',
    value: false,
    label: `${t('formAddress.noCountryside')}`,
  },
  {
    name: 'ruralArea',
    value: true,
    label: `${t('formAddress.yesCountryside')}`,
  },
]
const radiosClientLocalAddress = [
  {
    name: 'localAddress',
    value: true,
    label: `${t('formAddress.yesCountryside')}`,
  },
  {
    name: 'localAddress',
    value: false,
    label: `${t('formAddress.noCountryside')}`,
  },
]

// computed
const isFinancingDisabled = computed(() => {
  // TODO: Migrate this logic to BFF
  if (!financing.value) {
    return false
  }
  // Terminal financing (expired / reproved / canceled): read-only regardless of the current step.
  if (isTerminalReadOnly.value) {
    return true
  }
  if ([FinancingStatus.expired].includes(financing.value.status)) {
    return true
  }
  if (financing.value?.section_statuses?.customer_data?.status === 'approved') {
    return true
  }
  return [
    FinancingStatus.approved,
    FinancingStatus.reproved,
    FinancingStatus.expired,
    FinancingStatus.canceled,
  ].includes(financing.value.status) && financing.value.step === 'customer_data'
})

const isRegistrationDisabled = computed(() => {
  if (isTerminalReadOnly.value) {
    return true
  }
  if (!financing.value || !registration.value) {
    return false
  }
  return ['under_analysis', 'reproved', 'approved'].includes(registration.value?.status)
})

const hasAnyFormErrors = computed(() => {
  return validateFields(customerDocumentationForm)
    || validateFields(residencialAddressForm)
    || (radiosForms.values.client_local_address === false ? validateFields(installationAddressForm) : false)
    || validateFields(electricityBillForm)
    || validateFields(radiosForms)
})

const isSendDisabled = computed(() => {
  return isPendingUpdateRegistration.value
    || isAccessOpinionEvaluating.value
    || hasAnyFormErrors.value
    || isFinancingDisabled.value
    || isRegistrationDisabled.value
})

const isCancelDisabled = computed(() => {
  if (isTerminalReadOnly.value) {
    return true
  }
  if (!financing.value || !registration.value) {
    return false
  }

  return isPendingUpdateRegistration.value
    || isFinancingDisabled.value
    || registration.value?.status !== 'under_analysis'
})

const isFieldsDisabled = computed(() => {
  return isFinancingDisabled.value || isRegistrationDisabled.value
})

// functions get and saves
function validateFields(form: any) {
  return Object.keys(form.errors.value).length > 0
}

async function validateAllForms(): Promise<boolean> {
  const forms: any[] = [
    customerDocumentationForm,
    residencialAddressForm,
    electricityBillForm,
    radiosForms,
  ]

  if (hasOtherBeneficiaryUnitsModel.value) {
    forms.push(electricityBillsForm)
  }

  if (!radiosForms.values.client_local_address)
    forms.push(installationAddressForm)

  if (project.value?.guarantor) {
    forms.push(guarantorDocumentationForm)
    forms.push(guarantorAddressForm)
  }

  const results = await Promise.all(forms.map(form => form.validate()))
  const isValid = results.every(result => result.valid)

  return isValid
}

async function isValidForm() {
  const isValid = await validateAllForms()

  if (!isValid) {
    toast.createErrorToast({
      title: t('toast.incomplete.documentation.title'),
      description: t('toast.incomplete.documentation.description'),
      actionText: '',
    })
  }

  return isValid
}

async function createPayloadRegistration() {
  const residencialAddressPayload = {
    zipcode: residencialAddressForm.values.zipcode,
    street: residencialAddressForm.values.street,
    street_number: residencialAddressForm.values.street_number,
    complement: residencialAddressForm.values.complement || '',
    neighborhood: residencialAddressForm.values.neighborhood,
    city_id: residencialAddressForm.values.city_id,
    state_id: residencialAddressForm.values.state_id,
  }

  const installationAddressPayload = radiosForms.values.client_local_address
    ? { ...residencialAddressPayload, is_rural: radiosForms.values.rural_area }
    : {
        zipcode: installationAddressForm.values.zipcode,
        street: installationAddressForm.values.street,
        street_number: installationAddressForm.values.street_number,
        complement: installationAddressForm.values.complement || '',
        neighborhood: installationAddressForm.values.neighborhood,
        city_id: installationAddressForm.values.city_id,
        state_id: installationAddressForm.values.state_id,
        is_rural: radiosForms.values.rural_area,
      }

  const guarantorAddressPayload = {
    zipcode: guarantorAddressForm.values.zipcode,
    street: guarantorAddressForm.values.street,
    street_number: guarantorAddressForm.values.street_number,
    complement: guarantorAddressForm.values.complement || '',
    neighborhood: guarantorAddressForm.values.neighborhood,
    city_id: guarantorAddressForm.values.city_id,
    state_id: guarantorAddressForm.values.state_id,
  }

  const customerPayload = {
    full_name: customerDocumentationForm.values.full_name,
    birthdate: convertToISODate(customerDocumentationForm.values.birthdate),
    contact: {
      email: customerDocumentationForm.values.email,
      phone: customerDocumentationForm.values.phone,
    },
    documents: personSchema.extractDocumentEntries(customerDocumentationForm),
    address: residencialAddressPayload,
  }

  const guarantorObject = {
    full_name: guarantorDocumentationForm?.values?.full_name,
    birthdate: convertToISODate(guarantorDocumentationForm?.values?.birthdate),
    contact: {
      email: guarantorDocumentationForm?.values?.email,
      phone: guarantorDocumentationForm?.values?.phone,
    },
    documents: guarantorDocumentationForm?.values?.full_name ? personSchema.extractDocumentEntries(guarantorDocumentationForm) : null,
    address: guarantorAddressPayload,
  }
  const guarantorPayload = [undefined, ''].includes(guarantorDocumentationForm?.values?.full_name) ? null : guarantorObject

  const electricityBillsPayload = (electricityBillsForm.values.electricity_bills || []).map(bill => ({
    is_under_customer_name: bill.is_under_customer_name || false,
    additional_document_docstorage_id: bill.additional_document || null,
    holder_cpf: bill.is_under_customer_name ? null : bill.holder_cpf?.trim() || null,
    electricity_bill_docstorage_id: bill.electricity_bill_document || null,
    employment_proof_docstorage_id: bill.is_under_customer_name ? null : (bill.employment_proof_document || null),
  }))

  const projectPayload = {
    address: installationAddressPayload,
    documents: electricityBillSchema.extractDocumentEntries(electricityBillForm, { includeAccessOpinion: isAccessOpinionVisible.value }),
    comment: electricityBillForm.values.comment,
    electricity_bill: {
      is_under_customer_name: electricityBillForm.values.is_under_customer_name,
      holder_cpf: electricityBillForm.values.holder_cpf,
      distributor_id: electricityBillForm.values.distributor_id,
    },
    electricity_bills: (enableMultipleEnergyAccounts.value && radiosForms.values.has_other_beneficiary_units) ? electricityBillsPayload : [],
  }

  // Add electricity_bills to the root level of the payload (not inside project)

  return {
    project_id: projectId.value,
    person_type: String(project.value?.person_type).toLowerCase(),
    customer: customerPayload,
    project: projectPayload,
    company: null,
    guarantor: guarantorPayload,
  } as unknown as CustomerRegistrationInput
}

async function handleSave() {
  if (isTerminalReadOnly.value) {
    return
  }
  electricityBillForm.setFieldTouched('distributor_id', true)
  const isValidRegistrationForm = await isValidForm()

  if (!isValidRegistrationForm || !financing.value || !project.value) {
    return
  }

  // Sempre UF do endereço de instalação (se residência = instalação, usa o do cliente)
  const installationState = installationLocationForm.value.values.state

  const distributorGuard = validateDistributorInstallationState({
    distributorId: electricityBillForm.values.distributor_id,
    installationState,
  })
  if (!distributorGuard.valid) {
    showDistributorInstallationGuardError()
    return
  }

  const updateRegistrationPayload = await createPayloadRegistration()
  try {
    const response = await updateRegistration(updateRegistrationPayload)
    const success = response?.errors.length === 0

    if (success) {
      docUnderAnalysisModal.value = true
    }
    else {
      toast.createErrorToast({
        title: t('toast.error.send.title'),
        description: t('toast.error.send.description'),
      })
      const dataErrors = categorizeErrorsPF(
        dataUpdateRegistration.value?.errors as ErrorPayload[]
        || [],
      )
      showErrorsFieldsPF(
        dataErrors,
        customerDocumentationForm,
        guarantorDocumentationForm,
        residencialAddressForm,
        electricityBillForm,
        installationAddressForm,
        guarantorAddressForm,
        electricityBillsForm,
      )
    }
  }
  catch {
    docUnderAnalysisModal.value = false
    toast.createErrorToast({
      title: t('toast.error.send.title'),
      description: t('toast.error.send.description'),
    })
  }
}

function checkStageReproved() {
  if (props.stageReproved === '')
    blocked.value = false
  else
    blocked.value = props.stageReproved !== Formalization.CUSTOMER_DATA
}

function handleHasOtherBeneficiaryUnitsChange(value: string | number | boolean) {
  if (isTerminalReadOnly.value) {
    return
  }
  const boolValue = Boolean(value)
  if (boolValue === true) {
    showModal()
  }
  hasOtherBeneficiaryUnitsModel.value = boolValue
}

onMounted(() => {
  checkStageReproved()
})

watch(registration, (newValue) => {
  if (newValue) {
    radiosForms.setValues({
      client_local_address: newValue.project?.residencial_is_the_same_of_plant ?? true,
      rural_area: newValue.project?.address?.is_rural ?? false,
      has_other_beneficiary_units: (newValue.has_other_beneficiary_units as boolean) ?? false,
    }, false)
  }
}, { immediate: true })

watch(clientLocalAddressModel, (newValue, oldValue) => {
  if (isEqual(newValue, oldValue)) {
    return
  }

  if (newValue) {
    installationAddressForm.setValues({
      zipcode: residencialAddressForm.values.zipcode,
      street: residencialAddressForm.values.street,
      street_number: residencialAddressForm.values.street_number,
      complement: residencialAddressForm.values.complement,
      neighborhood: residencialAddressForm.values.neighborhood,
      city_id: residencialAddressForm.values.city_id,
      state_id: residencialAddressForm.values.state_id,
    }, false)
  }
  else {
    installationAddressForm.setValues({
      zipcode: undefined,
      street: undefined,
      street_number: undefined,
      complement: undefined,
      neighborhood: undefined,
      city: undefined,
      state: undefined,
      city_id: undefined,
      state_id: undefined,
    }, false)
  }
})

const submittedAt = computed(() => financing.value?.section_statuses?.customer_data?.submitted_at)
const completedAt = computed(() => financing.value?.section_statuses?.customer_data?.completed_at)
const reprovedAt = computed(() => financing.value?.section_statuses?.customer_data?.reproved_at)

const dateProps = useDateProps('registration', {
  completedAt,
  reprovedAt,
  submittedAt,
  blocked: isStageReprovedBlocked,
  financing,
})

const isApprovedPaymentModel = computed(() => financing.value?.section_statuses?.payment_model?.status === 'approved')

const isApprovedOrReprovedOrExpiredOrCanceledStatus = computed(() => {
  return [
    FinancingStatus.approved,
    FinancingStatus.reproved,
    FinancingStatus.expired,
    FinancingStatus.canceled,
  ].includes(financing.value?.status as FinancingStatus)
})

const isApprovedStepStatus = computed(() => {
  return financing.value?.section_statuses?.customer_data?.status === 'approved'
})

const hasShownStatus = computed(() => isApprovedPaymentModel.value || isApprovedOrReprovedOrExpiredOrCanceledStatus.value || isApprovedStepStatus.value)
const showDateProps = computed(() => submittedAt.value || completedAt.value || reprovedAt.value || !hasShownStatus.value)
</script>

<template>
  <CustomerElementAccordion
    :id="`accordion_${Formalization.CUSTOMER_DATA}`"
    :title="t('customer.client_data')"
    :position="String(position)"
    :tag="terminalTag ?? (financing && ((financing?.openable_steps?.registration && financing?.openable_steps?.documentation) || registration?.customer?.contact?.phone) && !isStageReprovedBlocked && !isTerminalLocked ? getStatusTag(t, financing?.section_statuses?.customer_data?.status) : null)"
    :blocked="isStageReprovedBlocked || isTerminalLocked || (!financing?.openable_steps?.registration && !financing?.openable_steps?.documentation && !registration?.customer?.contact?.phone)"
    :open-collapse="!(isStageReprovedBlocked || isTerminalLocked || (!financing?.openable_steps?.registration && !financing?.openable_steps?.documentation && !registration?.customer?.contact?.phone)) && stage === Formalization.CUSTOMER_DATA"
    :has-shown-status="hasShownStatus"
    v-bind="(showDateProps) ? dateProps : null"
    state="done"
    class="-mt-6"
  >
    <template #icon>
      <IconLock v-if="isStageReprovedBlocked || isTerminalLocked" />
    </template>

    <div>
      <p class="mt-2">
        {{ t('formalization.client.title') }}
      </p>
    </div>

    <div class="mt-sm !mb-0">
      <form class="flex flex-col gap-10">
        <PersonSectionForm
          :form="customerDocumentationForm"
          :title="t('customer.customer_section_title')"
          kind="customer"
          :disable-cpf="true"
          :disabled="isFieldsDisabled"
        />

        <SolDivider thickness="x-small" />

        <div v-show="project?.guarantor">
          <PersonSectionForm
            :form="guarantorDocumentationForm"
            :registration="registration"
            :title="t('customer.guarantor_section_title')"
            kind="guarantor"
            :disabled="isFieldsDisabled"
            :disable-cpf="true"
          />
        </div>

        <SolDivider v-show="project?.guarantor" thickness="x-small" />

        <AddressSectionForm
          :form="residencialAddressForm"
          :title="`${t('formalization.client.address.label')}`"
          kind="customer"
          :disabled="isFieldsDisabled"
        />

        <div class="flex flex-col gap-6">
          <div>
            <span class="font-bold text-3xs text-brand-primary-pure">{{ t('formAddress.local') }}</span>
            <SolRadioGroup
              id="clientLocalAddress"
              v-model="clientLocalAddressComputed"
              :value="radiosForms.values.client_local_address"
              title=""
              name="clientLocalAddress"
              v-bind="clientLocalAddressModeProps"
              direction="row"
              :disabled="isFieldsDisabled"
              :radios="radiosClientLocalAddress"
            />
          </div>

          <div v-show="!radiosForms.values.client_local_address">
            <AddressSectionForm
              :form="installationAddressForm"
              :title="t('formalization.address.installation.label')"
              kind="project"
              :disabled="isFieldsDisabled"
            />
          </div>

          <div v-show="project?.guarantor">
            <AddressSectionForm
              v-show="project?.guarantor"
              :form="guarantorAddressForm"
              :title="t('formalization.address.guarantor.label')"
              :registration="registration"
              :disabled="isFieldsDisabled"
              kind="guarantor"
            />
          </div>
        </div>

        <div>
          <span class="font-bold text-3xs text-brand-primary-pure">{{ t('formAddress.countryside') }}</span>
          <SolRadioGroup
            id="rural_area"
            v-model="ruralAreaComputed"
            title=""
            name="rural_area"
            v-bind="ruralAreaProps"
            direction="row"
            :disabled="isFieldsDisabled"
            :radios="radiosRuralArea"
          />
        </div>

        <SolDivider thickness="x-small" />

        <ElectricityBillSectionForm
          :form="electricityBillForm"
          :disabled="isFieldsDisabled"
          :access-opinion-mode="accessOpinionMode"
          :is-access-opinion-loading="isAccessOpinionEvaluating"
        />

        <!-- Radio para controlar múltiplas unidades -->
        <div v-if="enableMultipleEnergyAccounts" class="flex flex-col items-start gap-2 w-full">
          <SolRadioGroup
            id="has_other_beneficiary_units"
            :model-value="Boolean(hasOtherBeneficiaryUnitsModel)"
            :title="t('formalization.other_beneficiary_units.question')"
            name="has_other_beneficiary_units"
            direction="row"
            :disabled="isFieldsDisabled"
            :radios="[
              { name: 'has_other_beneficiary_units', value: true, label: t('formAddress.yesCountryside') },
              { name: 'has_other_beneficiary_units', value: false, label: t('formAddress.noCountryside') },
            ]"
            @update:model-value="handleHasOtherBeneficiaryUnitsChange"
          />
        </div>

        <ElectricityBillsSectionForm
          v-if="enableMultipleEnergyAccounts"
          :form="electricityBillsForm as Form"
          :disabled="isFieldsDisabled"
          :has-other-beneficiary-units="hasOtherBeneficiaryUnitsModel"
        />

        <div class="flex justify-end border-t border-neutral-high-medium pt-6 mt-8 gap-4 flex-col md:system:flex-row">
          <div :class="`flex ${showDateProps ? 'justify-between' : 'justify-end'} w-full gap-3 sm:system:items-center flex-col sm:system:flex-row`">
            <StatusDates
              v-if="showDateProps"
              :submitted-at="submittedAt"
              :completed-at="completedAt"
              :reproved-at="reprovedAt"
            />
            <div class="flex gap-4">
              <CancelStep
                step-to-cancel="registration"
                :project-id="projectId"
                :disabled="isCancelDisabled"
              />

              <SolButton
                id="customer_confirm"
                class="w-full md:system:w-auto order-1 md:system:order-2"
                :loading="isPendingUpdateRegistration"
                size="large"
                :disabled="isSendDisabled"
                @click="handleSave"
              >
                {{ t('app.send_data') }}
              </SolButton>
            </div>
          </div>
        </div>
      </form>
    </div>

    <DocumentationUnderAnalysisModal
      v-if="docUnderAnalysisModal"
      @close="() => docUnderAnalysisModal = false"
    />

    <MultiAccountAutoConsumptionModal
      :show="multiAccountModal"
      @close="closeModal"
    />
  </CustomerElementAccordion>
</template>

<style lang="scss" scoped>
.error-message {
  @apply text-feedback-negative-pure py-nano;
  @apply fonts-subtitle-small;
}

:deep(#input-text-input-phone) {
  @apply pl-xl;
}

[data-tooltip].tooltip:after {
  width: 250px;
  white-space: break-spaces;
}
</style>
